import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { gestao } from './api.ts';
import { useTextos } from './i18n.ts';
import { Consulta, Feedback, estadoTexto, useAcao } from './comum.tsx';

type A = {
  id: string;
  nome: string;
  url: string;
  segredo_ref: string;
  eventos: string[];
  estado: string;
  falhas_seguidas: number;
};
type E = {
  id: string;
  assinatura_id: string;
  evento_codigo: string;
  tentativas: number;
  estado: string;
};
type T = {
  id: string;
  evento_codigo: string;
  tentativa: number;
  status_http: number | null;
  erro: string | null;
  created_at: string;
  proxima_tentativa: string | null;
};
export function Webhooks() {
  const t = useTextos();
  const acao = useAcao();
  const [edicao, setEdicao] = useState<A | null>(null);
  const q = useQuery({
    queryKey: ['integracoes', 'webhooks'],
    queryFn: () => gestao<{
      assinaturas: A[];
      envios: E[];
      entregas: T[];
      avisos: {
        id: string;
        assinatura_id: string;
      }[];
      catalogo: string[];
    }>('webhooks_listar'),
  });
  const entrada = useQuery({
    queryKey: ['integracoes', 'entradas'],
    queryFn: () => gestao<{
      tokens: {
        id: string;
        tipo: string;
        ativo: boolean;
      }[];
      eventos: {
        id: string;
        conector: string;
        evento_externo_id: string;
        processado_em: string | null;
      }[];
    }>('entradas_listar'),
  });
  return <div className="integracoes">
    <Feedback acao={acao} />
    <form
      key={edicao?.id ?? 'novo'}
      onSubmit={e => {
        e.preventDefault();
        const f = new FormData(e.currentTarget);
        void acao.executar(() => gestao(edicao ? 'webhook_editar' : 'webhook_criar', {
          ...(edicao ? { id: edicao.id } : {}),
          nome: f.get('nome'),
          url: f.get('url'),
          segredo_ref: f.get('segredo_ref'),
          eventos: f.getAll('eventos'),
          estado: f.get('estado'),
        })).then(r => {
          if (r)
            setEdicao(null);
        });
      }}
    >

      <label>
        {t.nome}
        <input
          name="nome"
          defaultValue={edicao?.nome ?? ''}
          required
          maxLength={120}
        />
      </label>
      <label>
        {t.destino}
        <input
          name="url"
          type="url"
          defaultValue={edicao?.url ?? ''}
          required
          placeholder="https://"
        />
      </label>
      <label>
        {t.segredo}
        <input
          name="segredo_ref"
          defaultValue={edicao?.segredo_ref ?? ''}
          required
          placeholder="op://"
        />
      </label>

      <label>
        {t.eventos}
        <select
          name="eventos"
          multiple
          defaultValue={edicao?.eventos ?? []}
          required
        >
          {q.data?.catalogo.map(x => <option key={x}>
            {x}
          </option>)}
        </select>
      </label>
      <label>
        {t.estado}
        <select name="estado" defaultValue={edicao?.estado ?? 'ativa'}>
          {['ativa', 'pausada', 'desativada'].map(x => <option value={x} key={x}>
            {estadoTexto(x, t)}
          </option>)}
        </select>
      </label>
      <button disabled={acao.ocupado}>
        {edicao ? t.salvar : t.criar}
      </button>
      {edicao
        && <button type="button" onClick={() => setEdicao(null)}>
        {t.cancelar}
      </button>}
    </form>

    <Consulta consulta={q} vazio={!q.data?.assinaturas.length}>
      <div className="integracoes__tabela">
        <table>
          <thead>
            <tr>
              <th>
                {t.nome}
              </th>
              <th>
                {t.destino}
              </th>
              <th>
                {t.eventos}
              </th>
              <th>
                {t.estado}
              </th>
              <th>
                {t.tentativas}
              </th>
              <th>
                {t.detalhes}
              </th>
            </tr>
          </thead>
          <tbody>
            {q.data?.assinaturas.map(a => <tr key={a.id}>
              <td>
                {a.nome}
              </td>
              <td>
                {a.url}
              </td>
              <td>
                {a.eventos.join(', ')}
              </td>
              <td>
                {estadoTexto(a.estado, t)}
              </td>
              <td>
                {a.falhas_seguidas}
              </td>
              <td>
                <button onClick={() => setEdicao(a)}>
                  {t.editar}
                </button>
                <button
                  disabled={acao.ocupado}
                  onClick={() => void acao.executar(
                    () => gestao(
                      'webhook_excluir',
                      {
                        id: a.id,
                      },
                    ),
                  )}
                >
                  {t.excluir}
                </button>
              </td>
            </tr>)}
          </tbody>
        </table>
      </div>

      {!!q.data?.avisos.length
        && <aside role="alert">
        <h2>
          {t.avisos}
        </h2>
        {q.data.avisos.map(a => <p key={a.id}>
          {t.desativada}
          {': '}
          {a.assinatura_id}

          {' (20)'}
        </p>)}
      </aside>}

      <h2>
        {t.envios}
      </h2>
      <div className="integracoes__tabela">
        <table>
          <thead>
            <tr>
              <th>
                {t.eventos}
              </th>
              <th>
                {t.estado}
              </th>
              <th>
                {t.tentativas}
              </th>
              <th>
                {t.detalhes}
              </th>
            </tr>
          </thead>
          <tbody>
            {q.data?.envios.map(e => <tr key={e.id}>
              <td>
                {e.evento_codigo}
              </td>
              <td>
                {estadoTexto(e.estado, t)}
              </td>
              <td>
                {e.tentativas}
              </td>
              <td>
                <button
                  disabled={acao.ocupado}
                  onClick={() => void acao.executar(
                    () => gestao(
                      'webhook_reenviar',
                      {
                        id: e.id,
                      },
                    ),
                  )}
                >
                  {t.reenviar}
                </button>
              </td>
            </tr>)}
          </tbody>
        </table>
      </div>

      <h2>
        {t.entregas}
      </h2>
      <div className="integracoes__tabela">
        <table>
          <thead>
            <tr>
              <th>
                {t.eventos}
              </th>
              <th>
                {t.tentativas}
              </th>
              <th>
                {'HTTP'}
              </th>
              <th>
                {t.estado}
              </th>
              <th>
                {t.detalhes}
              </th>
            </tr>
          </thead>
          <tbody>
            {q.data?.entregas.map(e => <tr key={e.id}>
              <td>
                {e.evento_codigo}
              </td>
              <td>
                {e.tentativa}
              </td>
              <td>
                {e.status_http ?? t.desconectado}
              </td>
              <td>
                {e.erro ? t.falhou : t.entregue}
              </td>
              <td>
                {e.proxima_tentativa ? new Date(e.proxima_tentativa).toLocaleString() : ''}
              </td>
            </tr>)}
          </tbody>
        </table>
      </div>
    </Consulta>

    <h2>
      {t.entradas}
    </h2>
    <Consulta consulta={entrada} vazio={!entrada.data?.tokens.length}>
      <ul>
        {entrada.data?.tokens.map(x => <li key={x.id}>
          {x.tipo}
          {': '}
          {x.ativo ? t.ativa : t.entradaRevogada}

          {' '}
          {x.ativo
            && <button
            disabled={acao.ocupado}
            onClick={() => void acao.executar(
              () => gestao(
                'entrada_revogar',
                {
                  id: x.id,
                },
              ),
            )}
          >
            {t.revogar}
          </button>}
        </li>)}
      </ul>
      <div className="integracoes__tabela">
        <table>
          <thead>
            <tr>
              <th>
                {t.tipo}
              </th>
              <th>
                {t.eventos}
              </th>
              <th>
                {t.estado}
              </th>
            </tr>
          </thead>
          <tbody>
            {entrada.data?.eventos.map(x => <tr key={x.id}>
              <td>
                {x.conector}
              </td>
              <td>
                {x.evento_externo_id}
              </td>
              <td>
                {x.processado_em ? t.entregue : t.pendente}
              </td>
            </tr>)}
          </tbody>
        </table>
      </div>
    </Consulta>
  </div>;
}
